import { useContext } from 'react';
import styled, { css } from 'styled-components';
import {
  AppVimeoGalleryPlayVideoIcon,
  AppVimeoGalleryVimeoLogoLarge
} from '@elfsight/icons';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import { classnamePrefix } from '../../../../constants';
import { dictionary } from '../../../dictionary';
import { formatTime } from '../../../helpers';
import { Image } from '../../common';
import { currentVideoContext } from '../../currentVideoContext';
import { popupContext } from '../../popup';
import { videosContext } from '../VideosContext';
import { Player } from './Player';

export const VideoCard = ({
  data,
  showVimeoIcon,
  empty,
  lang,
  mobile,
  number = 0,
  count = 0
}) => {
  const {
    playMode,
    playlistNumbersVisible,
    videoElements,
    layout,
    textColor,
    accentColor,
    displayDeactivation
  } = useSettings();
  const { toggleShowPopup } = useContext(popupContext);
  const { isDrag } = useContext(videosContext);
  const {
    setCurrentVideo,
    currentVideo,
    playing,
    setPlaying,
    setPercent,
    setAutoplay,
    setVideoState
  } = useContext(currentVideoContext);

  const {
    name,
    link,
    pictures: { sizes },
    duration
  } = data;

  const onClick = (e) => {
    // crutch to solve problem SUP-1093
    if (playMode === 'inline') {
      e.preventDefault();
      return;
    }

    const isIconElement =
      e.target instanceof HTMLElement &&
      !!e.target.closest(`.${classnamePrefix}-video-card-icon`);

    if ((playMode !== 'vimeo' && !isIconElement) || isDrag) {
      e.preventDefault();

      if (!isDrag) {
        if (currentVideo !== data) {
          setCurrentVideo(data);
          setPlaying(false);
          setAutoplay(true);
          setPercent(0);
        } else if (currentVideo === data && !playing) {
          setVideoState('play');
        } else if (currentVideo === data && playing) {
          setVideoState('pause');
        }

        if (playMode === 'popup') {
          toggleShowPopup(true);
        }
      }
    }
  };

  const playIcon =
    videoElements.includes('playIcon') &&
    ((!playing && currentVideo === data) || currentVideo !== data) &&
    playMode !== 'inline';

  const vimeoIcon =
    showVimeoIcon &&
    videoElements.includes('vimeoIcon') &&
    ((!playing && currentVideo === data) || currentVideo !== data);

  const isCurrentVideo = currentVideo === data;
  const isPlayingCurrentVideo = playing && isCurrentVideo;
  const isActive = isCurrentVideo && playMode === 'player';

  return (
    <ContainerLink
      href={link}
      onClick={(e) => onClick(e)}
      target="_blank"
      rel="noreferrer nofollow noopener"
      title={name}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      _isActive={isActive}
      _playMode={playMode}
      _empty={empty}
      _isWidgetDeactivated={displayDeactivation}
      _layout={layout}
      _accentColor={accentColor}
      _isMobile={mobile}
    >
      {playlistNumbersVisible &&
        (layout === 'playlist' || layout === 'playlistVertical') && (
          <Number
            _count={count}
            _layout={layout}
            _textColor={textColor}
            _isCurrentVideo={isCurrentVideo}
          >
            {currentVideo !== data && number}
            {currentVideo === data && !playing && number}
            {currentVideo === data && playing && (
              <AppVimeoGalleryPlayVideoIcon
                width="12"
                height="12"
                fill="#fff"
              />
            )}
          </Number>
        )}

      <ImageWrapper
        _isActive={isActive}
        _accentColor={accentColor}
        _isPlayingCurrentVideo={isPlayingCurrentVideo}
        _layout={layout}
      >
        {playMode === 'inline' ? (
          <Player item={data} />
        ) : (
          <Image imagesArray={sizes} />
        )}

        {videoElements.includes('duration') && (
          <Time>{formatTime(duration)}</Time>
        )}

        {playIcon && (
          <PlayContainer _layout={layout}>
            <Play
              viewBox="0 0 20 20"
              preserveAspectRatio="xMidYMid"
              tabIndex="-1"
              _layout={layout}
            >
              <polygon className="fill" points="1,0 20,10 1,20" />
            </Play>
          </PlayContainer>
        )}

        {vimeoIcon && (
          <Icon
            title={
              dictionary[lang]?.['Open in Vimeo'] ||
              dictionary.en['Open in Vimeo']
            }
            _accentColor={accentColor}
          >
            <AppVimeoGalleryVimeoLogoLarge width="16" height="13" />
          </Icon>
        )}
      </ImageWrapper>

      {videoElements.includes('title') && (
        <Name
          _layout={layout}
          _textColor={textColor}
          _isMobileGrid={mobile && layout === 'grid'}
        >
          {name}
        </Name>
      )}
    </ContainerLink>
  );
};

const Name = styled.div.attrs({
  className: `${classnamePrefix}-video-card-name`
})`
  word-break: break-word;
  max-width: 600px;
  margin-left: 12px;
  text-transform: none;
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  min-height: 20px;
  color: ${({ _layout, _textColor }) =>
    _layout !== 'playlist' ? _textColor : 'rgb(0, 0, 0)'};

  ${({ _layout }) =>
    _layout !== 'playlist' &&
    css`
      font-weight: bold;
      margin: 10px 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      font-size: ${({ _isMobileGrid }) => (_isMobileGrid ? '13px' : '14px')};
      line-height: ${({ _isMobileGrid }) => (_isMobileGrid ? '16px' : '20px')};
    `}
`;

const ImageWrapper = styled.div.attrs({
  className: `${classnamePrefix}-video-card-image`
})`
  max-width: 120px;
  position: relative;
  width: 100%;
  display: flex;
  height: 68px;
  overflow: hidden;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  margin-left: 8px;

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    opacity: ${({ _isActive }) => (_isActive ? '1' : '0')};
    border: 3px solid ${({ _accentColor }) => _accentColor};
    background-color: ${({ _isPlayingCurrentVideo }) =>
      _isPlayingCurrentVideo && 'rgba(17, 17, 17, 0.7)'};
  }

  ${({ _layout }) =>
    _layout !== 'playlist' &&
    css`
      max-width: 100%;
      background-color: #000;
      height: 0;
      padding-top: 56%;
      margin-left: 0;
    `}
`;

const Play = styled.svg.attrs({
  className: `${classnamePrefix}-video-card-play`
})`
  fill: #fff;
  width: 12px;
  height: 12px;
  display: flex;

  ${({ _layout }) =>
    _layout !== 'playlist' &&
    css`
      width: 14px;
      height: 14px;
    `}
`;

const PlayContainer = styled.div.attrs({
  className: `${classnamePrefix}-video-card-playContainer`
})`
  position: absolute;
  padding: 7px 12px;
  background-color: rgba(17, 17, 17, 0.7);
  border-radius: 4px;
  transition: 0.3s ease background-color;

  ${({ _layout }) =>
    _layout !== 'playlist' &&
    css`
      padding: 9px 16px;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    `}
`;

const Time = styled.div.attrs({
  className: `${classnamePrefix}-video-card-time`
})`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.5);
  right: 4px;
  bottom: 4px;
  border-radius: 4px;
  color: #fff;
  padding: 2px;
  font-size: 11px;
  line-height: 12px;
  pointer-events: none;
  z-index: 1;
`;

const Icon = styled.div.attrs({
  className: `${classnamePrefix}-video-card-icon`
})`
  position: absolute;
  top: 6px;
  right: 6px;
  background-color: rgba(17, 17, 17, 0.7);
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  transition: background-color 0.3s ease;

  & svg {
    fill: #fff;
    pointer-events: none;
  }

  &:hover {
    background-color: ${({ _accentColor }) =>
      _accentColor ? _accentColor : ''};
  }
`;

const CardStyles = css`
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  padding: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;

  &:after {
    display: none;
  }
`;

const ActiveStyles = css`
  background-color: transparent;
`;

const ContainerStyles = css`
  display: flex;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  padding: 10px 0;
  text-decoration: none;
  position: relative;
  align-items: center;
  user-drag: none;
  user-select: none;
  flex-shrink: 0;
  opacity: ${({ _empty }) => (_empty ? '0' : '1')};
  pointer-events: ${({ _empty, _isWidgetDeactivated }) =>
    _empty || _isWidgetDeactivated ? 'none' : 'all'};

  &:after {
    content: '';
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    transition: width 0.3s ease;
    width: calc(100% + 12px);
    height: 1px;
    background-color: rgb(17, 17, 17);
    pointer-events: none;
  }

  &:hover,
  &:active,
  &:focus {
    text-decoration: none !important;
  }

  &:hover ${ImageWrapper} img {
    transform: scale(1.1);
  }

  &:hover ${PlayContainer} {
    background-color: ${({ _accentColor }) => _accentColor || ''};
  }

  & ${ImageWrapper} {
    height: ${({ _playMode }) => (_playMode === 'inline' ? 'auto' : '')};
    padding-top: ${({ _playMode }) => (_playMode === 'inline' ? '0' : '')};
  }
`;

const Number = styled.div.attrs({
  className: `${classnamePrefix}-video-card-number`
})`
  font-size: 12px;
  align-self: center;
  margin-left: 8px;
  flex-shrink: 0;
  display: inline-block;
  text-align: center;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  min-width: 12px;

  width: ${({ _count }) => `${_count.toString().length * 7}px`};
  color: ${({ _layout, _textColor }) =>
    _layout !== 'playlist' ? _textColor : '#111'};

  &:after {
    display: ${({ _isCurrentVideo }) =>
      _isCurrentVideo ? 'none' : 'inline-block'};
  }
`;

const ContainerLink = styled.a.attrs(({ _layout, _isActive }) => ({
  className: classNames(
    `${classnamePrefix}-video-card-container`,
    _isActive && `${classnamePrefix}-video-card-active`,
    _layout === 'grid' && `${classnamePrefix}-grid-card`
  )
}))`
  ${ContainerStyles}
  ${ActiveStyles}
  ${({ _layout }) => _layout === 'grid' && CardStyles}
`;
